import { IModule, IServiceProvider, IPromise, IQService, ITimeoutService, IIntervalService } from "angular";

/* eslint-disable prefer-rest-params */
/* eslint-disable @typescript-eslint/ban-types */
/**
 * This is a simple implementation of Zone.js (https://github.com/angular/angular/tree/master/packages/zone.js/), which
 * normally ships with Angular 2+. This implementation is made to work with Angular.js and is not as future-rich as the original
 * one. How does it work?
 *
 * Consider the following example:
 *
 * ```
 * class MyCtrl {
 *  constructor($http, $timeout, $q) {
 *    this.$http = $http;
 *    this.$timeout = $timeout;
 *    this.$q = $q;
 *
 *    const zoneA = Zone.current.fork("zoneA");
 *    const zoneB = Zone.current.fork("zoneB");
 *
 *    zoneA.run(this.init, this);
 *    zoneB.run(this.init, this);
 *  }
 *
 *  init() {
 *    console.log(`init: I am coming from zone ${Zone.current.name}`);
 *
 *    this.$http.get("some-url").then(() => {
 *      console.log(`$http: I am coming from zone ${Zone.current.name}`);
 *    });
 *
 *    this.$timeout(() => {
 *      console.log(`$timeout: I am coming from zone ${Zone.current.name}`);
 *    }, 1500);
 *
 *    const d = this.$q.defer();
 *    d.promise.then(() => {
 *      console.log(`$q: I am coming from zone ${Zone.current.name}`);
 *    });
 *    setTimeout(() => { d.resolve(); }, 2000);
 *  }
 * }
 * ```
 *
 * As we are dealing with callbacks, we have to transfer some context between them.
 * This is done by monkey-patching Angular.js services. Running the example above,
 * will print the correct zone in each callback.
 */

interface ZoneFrame {
  parent: ZoneFrame;
  zone: Zone;
}

let currentZoneFrame: ZoneFrame = null;

export class Zone {
  constructor(public name: string, public parent: Zone, public metadata: Record<string, unknown>) {}

  public static get current(): Zone {
    return currentZoneFrame.zone;
  }

  public static get root(): Zone {
    let zone = Zone.current;
    while (zone.parent) {
      zone = zone.parent;
    }
    return zone;
  }

  public printParentTree(): void {
    const names: string[] = [];
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let zone: Zone = this;
    while (zone) {
      names.push(zone.name);
      zone = zone.parent;
    }
    const tree = names.reduce((result, name, index) => result + "-".repeat(index) + " " + name + "\n", "").trim();
    // eslint-disable-next-line no-console
    console.log(tree);
  }

  public fork(name: string): Zone {
    // create the fork metadata using prototypical inheritance
    // from the current metadata
    const forkMetadata = function () {
      /* empty */
    };
    forkMetadata.prototype = this.metadata;

    return new Zone(name, this, new forkMetadata());
  }

  public run<TResult>(fn: Function, applyThis?: unknown, applyArgs?: unknown): TResult {
    currentZoneFrame = { parent: currentZoneFrame, zone: this };
    try {
      return fn.apply(applyThis, applyArgs);
    } finally {
      currentZoneFrame = currentZoneFrame.parent;
    }
  }

  public wrap<T extends Function>(callback: T): T {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const zone = this;
    return function (this: unknown) {
      return zone.run(callback, this, arguments);
    } as unknown as T;
  }
}

currentZoneFrame = { parent: null, zone: new Zone("<root>", null, {}) };

// eslint-disable-next-line @typescript-eslint/no-unused-vars

const isPromiseLike = (obj: any) => obj && angular.isFunction(obj.then);

export function configureZone(app: IModule): void {
  const splitChain = (chain: unknown): [string[], Function] => {
    if (angular.isArray(chain)) {
      const arr = chain as unknown[];
      return [arr.slice(0, arr.length - 1) as string[], arr[arr.length - 1] as Function];
    }
    if (angular.isFunction(chain)) {
      return [[], chain as Function];
    }
    throw new Error("Unexpected chain specified: " + chain);
  };

  const constructChain = (dependencies: string[], fn: Function): unknown => {
    return dependencies.length ? [...dependencies, fn] : fn;
  };

  // patch $q promises
  app.config([
    "$qProvider",
    function ($qProvider: IServiceProvider) {
      function patchCallback(zone: Zone, callback: Function): Function {
        let callbackPatch: Function;
        if (callback) {
          callbackPatch = function zoneAwareCallback(this: IQService) {
            let r = zone.run(callback, this, arguments);
            if (isPromiseLike(r)) {
              r = patchPromise(zone, r as IPromise<unknown>);
            }
            return r;
          };
        }
        return callbackPatch;
      }

      function patchPromise<T>(zone: Zone, promise: IPromise<T>): IPromise<T> {
        const oldThen = promise.then;
        promise.then = function zoneAwareThen<TResult>(
          successCallback: (promiseValue: T) => IPromise<TResult> | TResult,
          errorCallback?: (reason: unknown) => unknown,
          notifyCallback?: (state: unknown) => unknown
        ): IPromise<TResult> {
          const successCallbackPatch = patchCallback(zone, successCallback);
          const errorCallbackPatch = patchCallback(zone, errorCallback);
          const notifyCallbackPatch = patchCallback(zone, notifyCallback);

          const r = oldThen.call(this, successCallbackPatch, errorCallbackPatch, notifyCallbackPatch);
          return patchPromise(zone, r);
        };
        return promise;
      }

      const [dependencies, oldGet] = splitChain($qProvider.$get);
      $qProvider.$get = constructChain(dependencies, newGet);

      function newGet(this: IQService) {
        const oldQ: IQService = oldGet.apply(this, arguments);

        const newQ = function zoneAwareQ(this: IQService) {
          return patchPromise(Zone.current, oldQ.apply(this, arguments));
        };
        newQ.defer = function zoneAwareDefer(this: IQService) {
          const deferred = oldQ.defer.apply(this, arguments);
          deferred.promise = patchPromise(Zone.current, deferred.promise);
          return deferred;
        };
        newQ.when = function zoneAwareWhen(this: IQService) {
          return patchPromise(Zone.current, oldQ.when.apply(this, arguments));
        };
        newQ.reject = function zoneAwareReject(this: IQService) {
          return patchPromise(Zone.current, oldQ.reject.apply(this, arguments));
        };
        newQ.resolve = function zoneAwareResolve(this: IQService) {
          return patchPromise(Zone.current, oldQ.resolve.apply(this, arguments));
        };
        newQ.all = function zoneAwareAll(this: IQService) {
          return patchPromise(Zone.current, oldQ.all.apply(this, arguments));
        };
        return newQ;
      }
    },
  ]);

  // patch $timeout
  app.config([
    "$timeoutProvider",
    function ($timeoutProvider: IServiceProvider) {
      const [dependencies, oldGet] = splitChain($timeoutProvider.$get);
      $timeoutProvider.$get = constructChain(dependencies, newGet);

      function newGet(this: IServiceProvider) {
        const oldTimeout: ITimeoutService = oldGet.apply(this, arguments);

        const newTimeout = function zoneAwareTimeout(this: ITimeoutService, fn: unknown) {
          const zone = Zone.current;

          let r: IPromise<unknown>;
          if (!angular.isFunction(fn)) {
            r = oldTimeout.apply(this, arguments);
          } else {
            const newFn = function zoneAwareCallback(this: unknown) {
              return zone.run<IPromise<unknown>>(fn as Function, this, arguments);
            };

            // when calling the original $timeout, we have to replace the 1st argument
            // with our new function
            const args = [].slice.call(arguments);
            args[0] = newFn;
            r = oldTimeout.apply(this, args);
          }

          return r;
        };

        // copy other fields
        Object.keys(oldTimeout).forEach((propertyName) => {
          newTimeout[propertyName] = oldTimeout[propertyName];
        });
        return newTimeout;
      }
    },
  ]);

  // patch $interval
  app.config([
    "$intervalProvider",
    function ($intervalProvider: IServiceProvider) {
      const [dependencies, oldGet] = splitChain($intervalProvider.$get);
      $intervalProvider.$get = constructChain(dependencies, newGet);

      function newGet(this: IServiceProvider) {
        const oldInterval: IIntervalService = oldGet.apply(this, arguments);

        const newInterval = function zoneAwareInterval(this: IIntervalService, fn: Function) {
          const zone = Zone.current;

          const newFn = function zoneAwareCallback(this: unknown) {
            return zone.run<IPromise<unknown>>(fn, this, arguments);
          };

          // when calling the original $interval, we have to replace the 1st argument
          // with our new function
          const args = [].slice.call(arguments);
          args[0] = newFn;
          return oldInterval.apply(this, args);
        };

        // copy other fields
        Object.keys(oldInterval).forEach((propertyName) => {
          newInterval[propertyName] = oldInterval[propertyName];
        });
        return newInterval;
      }
    },
  ]);
}
